import React from "react";
import { Paper } from "./types";
// Hover / focus abstract beside a preprint card. The popover grows with the
// abstract, is capped at the viewport height (then scrolls inside), and
// flips to the left or below the card when there is no room on the right.
// The card's own "Abstract & authors" disclosure stays for touch and keyboard.
const GAP = 16,
  MARGIN = 12,
  WIDTH = 340;

export default function AbstractPreview({
  paper,
  children,
}: {
  paper: Paper;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLElement>(null);
  const [style, setStyle] = React.useState<React.CSSProperties>({});
  const id = "er-hover-abstract-" + paper.articleId;
  React.useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const place = () => {
      const r = ref.current!.getBoundingClientRect(),
        vw = window.innerWidth,
        vh = window.innerHeight,
        width = Math.min(WIDTH, vw - 2 * MARGIN);
      let left = r.right + GAP;
      if (left + width > vw - MARGIN) left = r.left - GAP - width;
      const beside = left >= MARGIN;
      if (!beside) left = Math.max(MARGIN, Math.min(r.left, vw - MARGIN - width));
      const base: React.CSSProperties = { position: "fixed", left, width, boxSizing: "border-box" };
      if (!beside && vh - (r.bottom + 8) - MARGIN < 160 && r.top - 8 - MARGIN > vh - (r.bottom + 8) - MARGIN) {
        // No room below a card sitting at the bottom of the viewport: open above it.
        setStyle({ ...base, bottom: vh - r.top + 8, maxHeight: r.top - 8 - MARGIN });
        return;
      }
      const top = beside ? Math.max(MARGIN, Math.min(r.top, vh - MARGIN - 120)) : r.bottom + 8;
      setStyle({ ...base, top, maxHeight: vh - top - MARGIN });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open]);
  return (
    <article
      ref={ref}
      className="er-paper"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null))
          setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
      aria-describedby={open ? id : undefined}
    >
      {children}
      {open && (
        <aside className="er-abstract-popover" id={id} role="tooltip" style={style}>
          <div className="er-kicker">Abstract</div>
          <h3>{paper.title}</h3>
          <p>{paper.abstract}</p>
          <p className="er-hover-hint">
            “Abstract & authors” in the card opens the full text for keyboard and touch.
          </p>
        </aside>
      )}
    </article>
  );
}
